import React from "react";
import { Input, InputNumber, Switch, Radio, Select, Space, Popconfirm, Rate, Segmented } from "antd";
import {
  CloseCircleFilled,
  BarsOutlined,
  StarOutlined,
  SmileOutlined,
  FrownFilled,
  MehFilled,
  SmileFilled,
} from "@ant-design/icons";
import TokenisedInput from "@Components/shared/TokenisedInput";
import TextArea from "antd/es/input/TextArea";
import { ITemplateField } from "@Models/IFeedbackTemplateDetails";

interface IProps {
    schema?: ITemplateField;
    handleDelete: (id: string) => Promise<void>;
    handleTypeChange: (id: string, newType: string) => Promise<void>;
    handleNameChange: (e: React.ChangeEvent<HTMLInputElement>, id: string) => Promise<void>;
    handleOptionChange: (id: string, option: number, value: string) => Promise<void>;
    handleOptionArrayChange: (id: string, values: string[]) => Promise<void>;
}

const TemplateDesignerRow: React.FC<IProps> = ({
  schema,
  handleDelete,
  handleTypeChange,
  handleNameChange,
  handleOptionChange,
  handleOptionArrayChange,
}: IProps) => {
  const { Option } = Select;
  const onTypeChange = (value: string) => {
    handleTypeChange(schema.id, value);
  };

  const RenderItem = (): JSX.Element[] => {
      const elements: JSX.Element[] = [];
      if (schema?.type === undefined) return elements;

      const headings = [
          <div className={"designer-heading designer-cell-1"}>Label</div>,
          <div className={"designer-heading designer-cell-2"}>Type</div>,
          <div className={"designer-heading designer-cell-3"}>Options</div>,
          <div className={"designer-heading designer-cell-4"}>Preview</div>,
          <div className={"designer-heading designer-cell-5"}>
              <Popconfirm
                  title="Are you sure you want to delete this item?"
                  onConfirm={() => handleDelete(schema.id)}
                  okText="Delete"
                  cancelText="Cancel"
                  cancelButtonProps={{ className: "secondary" }}
              >
                  <div style={{ pointerEvents: "auto" }} className={"designer-control designer-remove-control"}>
                      <CloseCircleFilled />
                  </div>
              </Popconfirm>
          </div>,
      ];

      // Input name
      elements.push(
          <div className={"designer-cell designer-cell-1"}>
              <Input
                  allowClear={true}
                  className={"designer-control"}
                  defaultValue={schema?.name}
                  key={schema?.id}
                  style={{ width: "120px" }}
                  onChange={(e) => handleNameChange(e, schema.id)}
              />
          </div>,
          <div className={"designer-cell designer-cell-2"}>
              <Select
                  className={"designer-control"}
                  style={{ width: "120px" }}
                  onChange={onTypeChange}
                  value={schema?.type}
              >
                  <Option value="text">Text</Option>
                  <Option value="textarea">Text area</Option>
                  <Option value="numeric">Numeric</Option>
                  <Option value="boolean">Yes / No</Option>
                  <Option value="option">Option</Option>
                  <Option value="likert">Likert</Option>
              </Select>
          </div>
      );

      // Input type-specific option fields
      const options: JSX.Element[] = [];

      switch (schema.type) {
          case "text":
              break;

          case "textarea":
              break;

          case "numeric":
              options.push(
                  <InputNumber
                      size={"small"}
                      addonBefore={<span style={{ display: "inline-block", width: "30px" }}>Min</span>}
                      placeholder={"Min"}
                      defaultValue={schema.options[0]}
                      onChange={(val) => handleOptionChange(schema.id, 0, val)}
                  />
              );
              options.push(
                  <InputNumber
                      size={"small"}
                      addonBefore={<span style={{ display: "inline-block", width: "30px" }}>Max</span>}
                      placeholder={"Max"}
                      defaultValue={schema.options[1]}
                      onChange={(val) => handleOptionChange(schema.id, 1, val)}
                  />
              );
              options.push(
                  <div style={{ margin: "10px" }}>
                      <span>
                          Show as percentage&nbsp;&nbsp;
                          <Switch
                              checkedChildren={"Yes"}
                              unCheckedChildren={"No"}
                              checked={schema.options[2] === "true"}
                              onChange={(val) => handleOptionChange(schema.id, 2, val.toString())}
                          />
                      </span>
                  </div>
              );
              break;

          case "boolean":
              break;

          case "option":
              options.push(
                  <div>
                      <TokenisedInput
                          initialOptions={schema.options}
                          maxOptions={10}
                          handleOnChange={(tags) => handleOptionArrayChange(schema.id, tags)}
                      />
                  </div>
              );
              break;

          case "likert":
              options.push(
                  <>
                      <Input
                          allowClear={true}
                          addonBefore="1"
                          size={"small"}
                          defaultValue={schema.options[0]}
                          onChange={(e) => handleOptionChange(schema.id, 0, e.target.value)}
                      />
                      <Input
                          allowClear={true}
                          addonBefore="2"
                          size={"small"}
                          defaultValue={schema.options[1]}
                          onChange={(e) => handleOptionChange(schema.id, 1, e.target.value)}
                      />
                      <Input
                          allowClear={true}
                          addonBefore="3"
                          size={"small"}
                          defaultValue={schema.options[2]}
                          onChange={(e) => handleOptionChange(schema.id, 2, e.target.value)}
                      />
                      <Input
                          allowClear={true}
                          addonBefore="4"
                          size={"small"}
                          defaultValue={schema.options[3]}
                          onChange={(e) => handleOptionChange(schema.id, 3, e.target.value)}
                      />
                      <Input
                          allowClear={true}
                          addonBefore="5"
                          size={"small"}
                          defaultValue={schema.options[4]}
                          onChange={(e) => handleOptionChange(schema.id, 4, e.target.value)}
                      />
                      <div style={{ margin: "10px" }}>
                          <span>
                              Show as &nbsp;&nbsp;
                              <Segmented
                                  options={[
                                      {
                                          value: "1",
                                          label: "Options",
                                          icon: <BarsOutlined />,
                                      },
                                      {
                                          value: "2",
                                          label: "Rating",
                                          icon: <StarOutlined />,
                                      },
                                      {
                                          value: "3",
                                          label: "Smileys",
                                          icon: <SmileOutlined />,
                                      },
                                  ]}
                                  onChange={(val) => handleOptionChange(schema.id, 5, val.toString())}
                              />
                          </span>
                      </div>
                  </>
              );
              break;
      }
      elements.push(<div className={"designer-cell designer-control designer-cell-3"}>{options}</div>);

      // Preview
      const preview: JSX.Element[] = [<p>{schema.name}</p>];
      switch (schema.type) {
          case "text":
              preview.push(<Input allowClear={true} />);
              break;

          case "textarea":
              preview.push(<TextArea allowClear={true} />);
              break;

          case "numeric":
              preview.push(
                  <InputNumber
                      controls={true}
                      min={schema.options[0] ?? 0} // Min
                      max={schema.options[1] ?? 100} // Max
                      addonAfter={schema.options[2] === "true" ? "%" : ""} // Display percentage
                  />
              );
              break;

          case "boolean":
              preview.push(<Switch checkedChildren={"Yes"} unCheckedChildren={"No"} />);
              break;

          case "option":
              preview.push(
                  <Radio.Group
                      name={schema.name}
                      defaultValue={schema.options[0] ?? null} // Each option is an option
                  >
                      <Space direction="vertical">
                          {schema.options.map((x, i) => (
                              <Radio key={i} value={x}>
                                  {x}
                              </Radio>
                          ))}
                          ;
                      </Space>
                  </Radio.Group>
              );
              break;

          case "likert":
              switch (schema.options[5]) {
                  default: // Options - radio buttons
                      preview.push(
                          <Radio.Group>
                              <Space direction="vertical">
                                  {schema.options.slice(0, 5).map((x, i) => {
                                      return (
                                          <Radio style={{ lineHeight: "12px" }} key={i} value={i}>
                                              {x}
                                          </Radio>
                                      );
                                  })}
                              </Space>
                          </Radio.Group>
                      );
                      break;

                  case "2": // Rating
                      const ratings = schema.options.slice(0, 5);
                      preview.push(
                          <span>
                              <Rate tooltips={ratings} />
                          </span>
                      );
                      break;

                  case "3": // Smileys
                      const smileys = schema.options.slice(0, 5);
                      const customIcons: Record<number, React.ReactNode> = {
                          1: <FrownFilled className={"smiley1"} />,
                          2: <FrownFilled className={"smiley2"} />,
                          3: <MehFilled className={"smiley3"} />,
                          4: <SmileFilled className={"smiley4"} />,
                          5: <SmileFilled className={"smiley5"} />,
                      };
                      preview.push(
                          <span>
                              <Rate
                                  tooltips={smileys}
                                  character={({ index }: { index: number }) => customIcons[index + 1]}
                              />
                          </span>
                      );
                      break;
              }
              break;
      }

      elements.push(<div className={"designer-cell designer-control designer-cell-4"}>{preview}</div>);

      return [<div className={"designer-row"}>{headings}</div>, <div className={"designer-row"}>{elements}</div>];
  };

  return <div key={"designer-row-" + schema?.id}>{RenderItem()}</div>;
};
export default TemplateDesignerRow;
